import axios from 'axios';
import {ImageData, ImageDetails, DuplicateImageGroup, DuplicateImagesResponse, MergeDuplicatesResponse} from '../types/Image'

const API_BASE_URL = process.env.REACT_APP_PATH_TO_API;

/**
 * Fetches gallery images with pagination and filtering
 */

export const fetchImages = async (includeGrouped: boolean = true, includeUngrouped: boolean = true): Promise<{images: ImageData[]}> => {
    if (!includeGrouped && !includeUngrouped) {
        throw new Error('either includeGrouped or includeUngrouped must be true');
    }

    try {
        const response = await axios.get(`${API_BASE_URL}/gallery`, {
            params: {
                includeGrouped: includeGrouped.toString(),
                includeUngrouped: includeUngrouped.toString()
            },
            headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });

        console.log(response.data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid parameters provided');
        }
        console.error('Failed to fetch gallery images:', error);
        throw new Error('Failed to load images. Please try again later.');
    }
};


export const deleteImages = async (imageKeys: string | string[]): Promise<{ deletedImages: string[]}> => {
    const keys = Array.isArray(imageKeys) ? imageKeys : [imageKeys];

    try {
        const response = await axios.delete(`${API_BASE_URL}/images`, {
            data: { imageKeys: keys },
            headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });

        console.log('Delete response:', response.data);

        return {
            deletedImages: response.data.deleted_images || [],
        };

    } catch (error) {
        console.error('Failed to delete images:', error);
        return { deletedImages: [] }; // Graceful handling with empty return
    }
};


export const resetImageMetadata = async (imageKeys: string | string[]): Promise<{ resetImages: string[]}> => {
    const keys = Array.isArray(imageKeys) ? imageKeys : [imageKeys];

    try {
        const response = await axios.patch(`${API_BASE_URL}/images`, {
            // todo: add an action: reset param?
            imageKeys: keys
        }, {
            headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });

        console.log('Metadata reset response:', response.data);
        return {
            resetImages: response.data.images_reset || [],
        };
        // return response.data;

    } catch (error) {
        console.error('Failed to reset image metadata:', error);
        return { resetImages: [] }; // Gracefully handle errors
    }
};


interface DownloadResponse {
    urls?: { url: string; filename: string }[];
    zipUrl?: string;
    message?: string;
    error?: string;
}

export const downloadImages = async (imageKeys: string | string[]): Promise<DownloadResponse> => {
    const keys = Array.isArray(imageKeys) ? imageKeys : [imageKeys];

    try {
        const response = await axios.post<DownloadResponse>(`${API_BASE_URL}/images/download`, {
            imageKeys: keys
        }, {
            headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });

        console.log(response);

        // Validate response format
        if (response.data.error) {
            throw new Error(response.data.error);
        }

        if (!response.data.urls && !response.data.zipUrl) {
            throw new Error('Invalid response format from server');
        }

        console.log('Download response:', response.data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid image IDs provided');
        }
        console.error('Failed to initiate download:', error);
        throw new Error('Failed to prepare download. Please try again later.');
    }
};


// todo: what is the promise type here?
export const fetchImageDetails = async (keys: string | string[]) => {
    let imageKeys = Array.isArray(keys) ? keys : [keys];

    try {
        const response = await axios.post(`${API_BASE_URL}/images`, {
            imageKeys: imageKeys
        }, {
            headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });

        // Validate response format
        if (response.data.error) {
            throw new Error(response.data.error);
        }

        console.log(response.data)

        // console.log('Download response:', response.data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid image IDs provided');
        }
        console.error('Failed to getImageDetails:', error);
        throw new Error('Failed to get image details. Please try again later.');
    }
};

/**
 * Fetches detailed metadata for a specific image
 * @param key - The unique identifier for the image
 * @returns Promise<{image: ImageDetails}> - A promise that resolves to an object containing the image details
 */
export const fetchImageMetadata = async (key: string): Promise<{image: ImageDetails}> => {
    console.log('Fetching metadata for image:', key);
    try {
        const response = await axios.get(`${API_BASE_URL}/images/${key}`, {
            headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });

        // Validate response format
        if (response.data.error) {
            throw new Error(response.data.error);
        }

        // Convert null values to undefined in the response
        const processedData = convertNullToUndefined(response.data);
        
        console.log('Image metadata response (after null conversion):', processedData);
        
        // Return the processed response
        return processedData;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid image ID provided');
        }
        console.error('Failed to fetch image metadata:', error);
        throw new Error('Failed to get image metadata. Please try again later.');
    }
};

/**
 * Helper function to recursively clean an object by removing null values and empty objects
 * @param obj - The object to process
 * @returns The processed object with null values and empty objects removed
 */
const convertNullToUndefined = (obj: any): any => {
    // If the input is null, return undefined
    if (obj === null) return undefined;
    
    // If the input is not an object or is a Date, return it as is
    if (typeof obj !== 'object' || obj instanceof Date) return obj;
    
    // Handle arrays by mapping each element and filtering out undefined values
    if (Array.isArray(obj)) {
        const processedArray = obj
            .map(item => convertNullToUndefined(item))
            .filter(item => item !== undefined);
        
        // If the array is empty after processing, return undefined
        return processedArray.length > 0 ? processedArray : undefined;
    }
    
    // Process object properties
    const result: any = {};
    let hasValidProperties = false;
    
    for (const key in obj) {
        // Always keep the 'key' property as it's required
        if (key === 'key') {
            result[key] = obj[key];
            hasValidProperties = true;
            continue;
        }
        
        // Process the value
        const processedValue = obj[key] === null ? undefined : convertNullToUndefined(obj[key]);
        
        // Only add non-undefined values to the result
        if (processedValue !== undefined) {
            result[key] = processedValue;
            hasValidProperties = true;
        }
    }
    
    // Special handling for location object - if it exists but has no properties, return undefined
    if (obj.hasOwnProperty('location') && Object.keys(result.location || {}).length === 0) {
        delete result.location;
    }
    
    // If the object has no valid properties after processing, return undefined
    return hasValidProperties ? result : undefined;
};

/**
 * Fetches all duplicate images for the current user
 * @returns Promise<DuplicateImagesResponse> - A promise that resolves to an object containing arrays of duplicate groups
 */
export const fetchAllDuplicates = async (): Promise<DuplicateImagesResponse> => {
    console.log('Fetching all duplicates');
    try {
        const response = await axios.get(`${API_BASE_URL}/images/duplicates`, {
            headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });

        // Validate response format
        if (response.data.error) {
            throw new Error(response.data.error);
        }

        console.log('All duplicates fetched successfully', {
            count: response.data.duplicates?.length
        });

        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid request');
        }
        console.error('Failed to fetch all duplicates:', error);
        throw new Error('Failed to fetch duplicates. Please try again later.');
    }
};

/**
 * Fetches duplicates within a specific set of images
 * @param imageKeys - Array of image keys to check for duplicates
 * @returns Promise<DuplicateImagesResponse> - A promise that resolves to an object containing arrays of duplicate groups with thumbnail URLs
 */
export const fetchDuplicatesInImages = async (imageKeys: string[]): Promise<DuplicateImagesResponse> => {
    console.log('Fetching duplicates in images:', { imageCount: imageKeys.length });
    try {
        const response = await axios.post(
            `${API_BASE_URL}/images/duplicates`,
            { image_keys: imageKeys },
            {
                headers: {
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        // Validate response format
        if (response.data.error) {
            throw new Error(response.data.error);
        }

        console.log('Duplicates in images fetched successfully', {
            count: response.data.duplicates?.length
        });

        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid request');
        }
        console.error('Failed to fetch duplicates in images:', error);
        throw new Error('Failed to check for duplicates. Please try again later.');
    }
};

/**
 * Merges duplicate images, keeping one and marking others as merged
 * @param keepImageKey - The key of the image to keep
 * @returns Promise<MergeDuplicatesResponse> - A promise that resolves to an object containing the result of the merge operation
 */
export const mergeDuplicates = async (keepImageKey: string): Promise<MergeDuplicatesResponse> => {
    console.log('Merging duplicates for image:', { keepImageKey });
    try {
        const response = await axios.patch(
            `${API_BASE_URL}/images/duplicates`,
            { keep_image_key: keepImageKey },
            {
                headers: {
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        // Validate response format
        if (response.data.error) {
            throw new Error(response.data.error);
        }

        console.log('Duplicates merged successfully', {
            keptKey: response.data.kept_key,
            mergedCount: response.data.merged_keys?.length
        });

        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid request');
        }
        console.error('Failed to merge duplicates:', error);
        throw new Error('Failed to merge duplicates. Please try again later.');
    }
};